import { useState, useMemo, useEffect } from 'react';
import {
  FaExclamationTriangle,
  FaRegArrowAltCircleRight,
} from 'react-icons/fa';
import RadioInput from '../inputs/RadioInput';
import TextInput from '../inputs/TextInput';
import Button from '../inputs/Button';
import ErrorText from '../ErrorText';
import {
  SET_ADJUSTMENT_ERROR,
  SET_RECORDS_INCLUDED,
} from '../../reducers/builderReducer';
import { useAuth } from '../../hooks/useAuth';
import { calculateProductPrice, getProductRate } from '../../helpers/pricing';

const ENV = import.meta.env.VITE_ENV;

export default function MyAudience({
  hasStateChanged,
  onGetCountsClick,
  recordsAvailable,
  recordsIncluded,
  incomingRecordsIncluded,
  error: parentError,
  canBuy: parentCanBuy,
  onBuyClick,
  // ATAD-134. Both computed in AudienceBuilder — the threshold and the
  // freshness gate live in one place so they can't drift between the badge
  // and the Continue Order warning.
  showOversizeBadge,
  universeSharePct,
  builderDispatch,
}) {
  const { user } = useAuth();

  const [price, setPrice] = useState(0);
  const [adjustmentError, setAdjustmentError] = useState<string | undefined>();
  const [adjustment, setAdjustment] = useState(incomingRecordsIncluded || 0);
  const [adjustmentType, setAdjustmentType] = useState('quantity');
  const [buyValidationError, setBuyValidationError] = useState<
    string | undefined
  >();

  useEffect(() => {
    setAdjustmentType('quantity');
    setAdjustment(incomingRecordsIncluded || recordsAvailable);
  }, [incomingRecordsIncluded, recordsAvailable]);

  const error = useMemo(
    () => parentError || buyValidationError,
    [parentError, buyValidationError]
  );

  const canBuy = useMemo(
    () => parentCanBuy && price != null && recordsIncluded && !adjustmentError,
    [adjustmentError, parentCanBuy, price, recordsIncluded]
  );

  function handleAdjustmentTypeChange(e) {
    const { value } = e.target;
    setAdjustmentType(value);
  }

  function handleAdjustmentChange(e) {
    const { value } = e.target;
    setAdjustment(value);
  }

  useEffect(() => {
    function setRecordsIncluded(recordsIncluded) {
      builderDispatch({
        type: SET_RECORDS_INCLUDED,
        payload: {
          recordsIncluded: Math.floor(recordsIncluded),
        },
      });
    }

    if (adjustment && recordsAvailable > 0) {
      if (adjustmentType === 'budget') {
        const rate = getProductRate('audience', user.plan);

        setRecordsIncluded((+adjustment / rate) * 1000);
        setPrice(
          +adjustment >
            calculateProductPrice('audience', recordsAvailable, user.plan)
            ? calculateProductPrice('audience', recordsAvailable, user.plan)
            : adjustment
        );

        if (adjustment < 84) {
          setAdjustmentError(
            'That budget is too low. Please increase your budget to at least $84.'
          );
          return;
        }
        if ((+adjustment / rate) * 1000 > recordsAvailable) {
          setAdjustmentError('That budget is too high.');
          return;
        }

        setAdjustmentError(null);
        return;
      }
      if (adjustmentType === 'quantity') {
        setRecordsIncluded(+adjustment);
        setPrice(calculateProductPrice('audience', +adjustment, user.plan));

        if (ENV !== 'local' && adjustment < 2000) {
          setAdjustmentError(
            adjustment === recordsAvailable
              ? 'This audience is too small. Please adjust your filters to create a larger audience.'
              : 'That quantity is too low. Please increase your quantity to at least 2000 records.'
          );
          return;
        }
        if (adjustment > recordsAvailable) {
          setAdjustmentError(
            'That quantity is higher than the available records. Please decrease your quantity.'
          );
          return;
        }

        setAdjustmentError(null);
        return;
      }
    }

    setPrice(calculateProductPrice('audience', recordsAvailable, user.plan));
  }, [
    adjustment,
    adjustmentType,
    recordsAvailable,
    recordsIncluded,
    user.plan,
    builderDispatch,
  ]);

  useEffect(() => {
    builderDispatch({
      type: SET_ADJUSTMENT_ERROR,
      payload: adjustmentError,
    });
  }, [adjustmentError, builderDispatch]);

  function handleBuyClick() {
    if (price == null || isNaN(price) || !recordsIncluded) {
      setBuyValidationError(
        'Please set a quantity or budget to purchase this audience.'
      );
      return;
    }
    onBuyClick(recordsIncluded);
  }

  return (
    <div className="flex flex-col p-2">
      <div className="mb-2">
        <h4 className="mb-0">My Audience</h4>
      </div>
      <div className="flex flex-col bg-gray-100 rounded-half p-4">
        <div className="bg-white rounded-half py-6 px-4 text-center">
          <p className="text-xl font-bold">
            {recordsAvailable?.toLocaleString()}
          </p>
          <p className="text-gray-400">Records Available</p>
          {/* Warning styling follows the established app idiom (text-orange +
              icon, no fill) — see FileStatusBadge and DistributionSummary. The
              Tailwind palette here is a closed set with no amber/yellow. */}
          {showOversizeBadge && universeSharePct != null && (
            <p
              className="mt-2 inline-flex items-center gap-1.5 text-sm text-orange"
              title={`About ${universeSharePct}% of our estimate of the US consumer universe. The estimate shifts slightly each quarter.`}
            >
              <FaExclamationTriangle className="text-base" aria-hidden="true" />
              <span>~{universeSharePct}% of US consumers</span>
            </p>
          )}
        </div>
        <div className="py-4">
          <Button
            className="w-full"
            type="button"
            secondary={!hasStateChanged}
            primary={hasStateChanged}
            onClick={onGetCountsClick}
          >
            Get counts
          </Button>
        </div>
        <div className="">
          <p className="font-bold pb-2">Too big? Adjust your:</p>
          <ul className="space-y-2">
            <li>
              <div>
                <RadioInput
                  value="quantity"
                  checked={adjustmentType === 'quantity'}
                  onChange={handleAdjustmentTypeChange}
                />{' '}
                Quantity
              </div>
            </li>
            <li>
              <div>
                {/*Disabled Budget Control feature*/}
                {/*<RadioInput*/}
                {/*  value="budget"*/}
                {/*  checked={adjustmentType === 'budget'}*/}
                {/*  onChange={handleAdjustmentTypeChange}*/}
                {/*/>{' '}*/}
                {/*Budget*/}
              </div>
            </li>
          </ul>
          <div className="py-4 pb-12">
            <TextInput
              className="w-full"
              type="number"
              step="1000"
              placeholder="Value"
              value={adjustment}
              onChange={handleAdjustmentChange}
              small
              iconRight={
                <button type="button">
                  <FaRegArrowAltCircleRight />
                </button>
              }
            />
          </div>
          {adjustmentError && (
            <div className="w-56 -mt-10 mb-2 text-xs text-error">
              {adjustmentError}
            </div>
          )}
          <div className="bg-white rounded-half py-6 px-4 text-center">
            <p className="text-xl font-bold">
              {parseFloat(recordsIncluded).toLocaleString(undefined, {
                maximumFractionDigits: 0,
              })}
            </p>
            <p className="text-gray-400">Records Included for</p>
            <p className="font-bold">
              $
              {parseFloat(price).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </p>
          </div>
        </div>
      </div>
      <div className="pt-5">
        <Button
          className="w-full"
          type="button"
          primary
          disabled={!canBuy}
          onClick={handleBuyClick}
        >
          Continue Order
        </Button>
      </div>
      <ErrorText message={error} className="pt-5 w-72" />
    </div>
  );
}
